/* See https://ui.shadcn.com/docs/dark-mode/vite */

import { createContext, useContext, useEffect } from "react";

type ColorScheme = "light" | "dark" | "system";

type ColorSchemeProviderState = {
  colorScheme: ColorScheme;
  // We've disabled this in favor of storing the color scheme in user preferences.
  // We can add it back if we need to.
  // setColorScheme: (colorScheme: ColorScheme) => void;
};

const initialState: ColorSchemeProviderState = {
  colorScheme: "system",
};

const ColorSchemeProviderContext = createContext<ColorSchemeProviderState>(initialState);

/** Set a color scheme for CSS to pick up, and expose the current color scheme with a context-based hook to descendants. */
export function ColorSchemeProvider({
  children,
  // TODO: https://linear.app/big-basin-labs/issue/BIG-500/preferences-weigh-default-handling
  colorScheme = "light", // TODO: default to system when we have actual dark mode support
  ...props
}: {
  children: React.ReactNode;
  colorScheme?: ColorScheme;
}) {
  useEffect(() => {
    const root = window.document.documentElement;

    root.classList.remove("light", "dark");

    if (colorScheme === "system") {
      const systemColorScheme = window.matchMedia("(prefers-color-scheme: dark)").matches
        ? "dark"
        : "light";

      root.classList.add(systemColorScheme);
      return;
    }

    root.classList.add(colorScheme);
  }, [colorScheme]);

  const value = {
    colorScheme,
  };

  return (
    <ColorSchemeProviderContext.Provider {...props} value={value}>
      {children}
    </ColorSchemeProviderContext.Provider>
  );
}

export const useColorScheme = () => {
  const context = useContext(ColorSchemeProviderContext);

  if (context === undefined) {
    throw new Error("useColorScheme must be used within a ColorSchemeProvider");
  }

  return context;
};
